import { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { getVendaiTimePeriodSaleTable } from "../../api/dashboardAPI";

// Dönem satırları — veride bulunanlar bu sırayla gösterilir.
const PERIOD_ORDER = [
  "today",
  "yesterday",
  "thisWeek",
  "lastWeek",
  "thisMonth",
  "lastMonth",
  "thisYear",
  "lastYear",
];

// Backend "veri yok" için -1 / "-1 ₺" kullanıyor → null (tabloda "-" görünür).
const normalize = (v) => {
  if (typeof v === "string") {
    return /^-1(\s|₺|$)/.test(v.trim()) ? null : v;
  }
  if (typeof v === "number") {
    return v < 0 ? null : v;
  }
  return v ?? null;
};

// Endpoint verisini WidgetTable yapısına ({ headers, rowHeaders, cells, title }) dönüştür.
function buildTable(periods, fields, title) {
  const rowHeaders = PERIOD_ORDER.filter((k) => periods?.[k]);
  const cells = rowHeaders.map((k) =>
    fields.map((f) => normalize(periods[k][f]))
  );
  // OLD tablo ile aynı düzen: Toplam (sum) en başta.
  return { title, headers: ["sum", "card", "cash", "app"], rowHeaders, cells };
}

// Dashboard'daki WidgetTable ile birebir aynı görünüm.
function SummaryTable({ tableData }) {
  const { t } = useTranslation();

  const headerLabel = (header) => {
    if (header === "card") return t("widgets.card", "Kart");
    if (header === "cash") return t("widgets.cash", "Nakit");
    if (header === "app") return t("widgets.app", "Mobil");
    if (header === "sum") return t("widgets.sum", "Toplam");
    return header;
  };

  const rowHeaderLabel = (rowHeader, rowIndex) => {
    if (!rowHeader) return `${rowIndex + 1}`;
    switch (rowHeader) {
      case "today":
        return t("widgets.today", "Bugün");
      case "yesterday":
        return t("widgets.yesterday", "Dün");
      case "thisWeek":
        return t("widgets.thisWeek", "Bu Hafta");
      case "lastWeek":
        return t("widgets.lastWeek", "Geçen Hafta");
      case "thisMonth":
        return t("widgets.thisMonth", "Bu Ay");
      case "lastMonth":
        return t("widgets.lastMonth", "Geçen Ay");
      case "thisYear":
        return t("widgets.thisYear", "Bu Yıl");
      case "lastYear":
        return t("widgets.lastYear", "Geçen Yıl");
      default:
        return rowHeader;
    }
  };

  // Kolon genişlikleri (%) — Toplam geniş, Mobil dar (toplam 100).
  // Sol baştaki dönem (satır) sütunu LABEL_WIDTH alır; bilinmeyen başlık eşit pay.
  const LABEL_WIDTH = 16;
  const HEADER_WIDTHS = { sum: 26, card: 21, cash: 21, app: 16 };
  const evenWidth = tableData?.headers?.length
    ? (100 - LABEL_WIDTH) / tableData.headers.length
    : 20;
  const labelWidth = `${LABEL_WIDTH}%`;
  const widthFor = (header) => `${HEADER_WIDTHS[header] ?? evenWidth}%`;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 0,
        borderRadius: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        marginBottom: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: "none",
        border:
          theme.palette.mode === "dark"
            ? "1px solid #404040"
            : "1px solid #e0e0e0",
        transition: "all 0.3s ease-in-out",
        position: "relative",
      })}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#2d3830" : "#70d470ff",
          color: theme.palette.mode === "dark" ? "#ffffff" : "#ffffff",
          padding: "12px 16px",
        })}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {tableData?.title === "sumSales"
            ? t("widgets.sumSales", "Toplam Satış")
            : tableData?.title === "salesCount"
            ? t("widgets.salesCount", "Satış Adeti")
            : tableData?.title
            ? t(`widgets.${tableData.title}`, tableData.title)
            : ""}
        </Typography>
      </Box>
      <TableContainer>
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow sx={{ height: 44 }}>
              <TableCell
                sx={(theme) => ({
                  fontWeight: 500,
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#2a2a2a" : "#fafafa",
                  color: theme.palette.text.primary,
                  padding: "4px 8px",
                  fontSize: "0.85rem",
                  borderBottom:
                    theme.palette.mode === "dark"
                      ? "2px solid #555555"
                      : "2px solid #c0c0c0",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  textAlign: "right",
                  width: labelWidth,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                })}
              ></TableCell>
              {tableData?.headers?.map((header, index) => (
                <TableCell
                  key={index}
                  sx={(theme) => ({
                    fontWeight: 500,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#fafafa",
                    color: theme.palette.text.primary,
                    padding: "4px 8px",
                    textAlign: "center",
                    fontSize: "0.85rem",
                    borderBottom:
                      theme.palette.mode === "dark"
                        ? "2px solid #555555"
                        : "2px solid #c0c0c0",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    width: widthFor(header),
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  })}
                >
                  {headerLabel(header)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.cells?.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                sx={(theme) => ({
                  height: 44,
                  "&:nth-of-type(even)": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#222b31" : "#f5f5f5",
                  },
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#35596b" : "#e0f2f1",
                  },
                  transition: "background-color 0.2s ease",
                  borderBottom:
                    theme.palette.mode === "dark"
                      ? "1px solid #555555"
                      : "1px solid #c0c0c0",
                })}
              >
                <TableCell
                  sx={(theme) => ({
                    fontWeight: 500,
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2a2a2a" : "#fafafa",
                    color: theme.palette.text.primary,
                    padding: "4px 8px",
                    textAlign: "center",
                    fontSize: "0.8rem",
                    borderRight:
                      theme.palette.mode === "dark"
                        ? "1px solid #555555"
                        : "1px solid #c0c0c0",
                    width: labelWidth,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  })}
                >
                  {rowHeaderLabel(tableData?.rowHeaders?.[rowIndex], rowIndex)}
                </TableCell>
                {row.map((cell, cellIndex) => (
                  <TableCell
                    key={cellIndex}
                    sx={(theme) => ({
                      textAlign: "right",
                      padding: "4px 8px",
                      fontSize: "0.8rem",
                      color:
                        cell !== null
                          ? theme.palette.mode === "dark"
                            ? "#e0f7fa"
                            : "#263238"
                          : theme.palette.mode === "dark"
                          ? "#666"
                          : "#b0b8c1",
                      fontWeight: cell !== null ? "600" : "400",
                      borderRight:
                        cellIndex === row.length - 1
                          ? "none"
                          : theme.palette.mode === "dark"
                          ? "1px solid #555555"
                          : "1px solid #c0c0c0",
                      width: widthFor(tableData?.headers?.[cellIndex]),
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {cell !== null ? (
                      <Typography
                        variant="body1"
                        fontWeight={400}
                        sx={(theme) => ({
                          display: "block",
                          width: "100%",
                          textAlign: "right",
                          color:
                            theme.palette.mode === "dark"
                              ? "#b3e5fc"
                              : "#000000",
                          fontSize: "0.9rem",
                          fontFamily: "'Roboto Mono', monospace",
                          lineHeight: 1.1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >
                        {typeof cell === "number" ? cell.toLocaleString() : cell}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body1"
                        fontWeight={400}
                        sx={(theme) => ({
                          display: "block",
                          width: "100%",
                          textAlign: "right",
                          color:
                            theme.palette.mode === "dark" ? "#666" : "#bdbdbd",
                          fontSize: "0.9rem",
                          lineHeight: 1.1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        })}
                      >
                        -
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function VendaiSaleTables({ periodId = 3 }) {
  const [periods, setPeriods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await getVendaiTimePeriodSaleTable(periodId);
        if (active) setPeriods(data || null);
      } catch (e) {
        console.error("[VendaiSaleTables] error:", e);
        if (active) setError(e.message || "Veri alınamadı");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [periodId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ my: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!periods) {
    return null;
  }

  const sumSales = buildTable(
    periods,
    ["totalPrice", "cardPrice", "cashPrice", "mobilePrice"],
    "sumSales"
  );
  const salesCount = buildTable(
    periods,
    ["totalCount", "cardCount", "cashCount", "mobileCount"],
    "salesCount"
  );

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        <SummaryTable tableData={sumSales} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        <SummaryTable tableData={salesCount} />
      </Box>
    </Box>
  );
}
